import AuthenticateUI from '@/components/AuthenticateUI';
import {
  APP_ROUTES,
  APPLICATION_STATUS,
  CLARITY_TAG_NAME,
  ENVELOPE_STATUS,
} from '@/constants/constants';
import { useGetBoardPermissionQuery } from '@/hooks/api/useGetBoardPermissionQuery';
import useGetContextQuery from '@/hooks/api/useGetContextQuery';
import { useGetEnvelopeQuery } from '@/hooks/api/useGetEnvelopeQuery';
import useGetFileListQuery from '@/hooks/api/useGetFileListQuery';
import { useGetProgressQuery } from '@/hooks/api/useGetProgressQuery';
import { useGetSessionToken } from '@/hooks/api/useGetSessionToken';
import useFeatureBasedPricing from '@/hooks/useFeatureBasedPricing';
import { updateBackOfficeInstalledStat } from '@/services/board';
import { setClarityCustomTag } from '@/utils/clarity.utils';
import { getPlatform } from '@/utils/common';
import { Loader } from '@vibe/core';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const NewOnboardingRoot = () => {
  const { data: sessionToken, isLoading: isSessionTokenLoading } =
    useGetSessionToken();
  const { data: context, isLoading: isContextLoading } = useGetContextQuery({
    onSuccess: (data) => {
      if (!data) return;

      const { versionData } = data.appVersion || {};
      if (versionData) {
        const version = `v${versionData?.major || 0}.${
          versionData?.minor || 0
        }.${versionData?.patch || 0}`;
        updateBackOfficeInstalledStat(version);
      }

      const platform = getPlatform(data.mode);
      setClarityCustomTag(CLARITY_TAG_NAME.PLATFORM, platform);
    },
  });
  const { isLoadingSubscriptionData } = useFeatureBasedPricing();
  const navigate = useNavigate();

  const getProgressQuery = useGetProgressQuery(context, {
    refetchOnWindowFocus: false,
  });
  const {
    data: progress,
    isError: isProgressError,
    refetch: refetchProgress,
  } = getProgressQuery;

  const { data: envelopeDetails, isLoading: isGetEnvelopeDetailsLoading } =
    useGetEnvelopeQuery(context, {
      enabled: !!progress,
      refetchOnWindowFocus: false,
    });

  const { data: fileList, isLoading: isFileListLoading } = useGetFileListQuery(
    envelopeDetails?._id,
    context?.itemId,
    {
      enabled: (!!envelopeDetails?._id && !!context?.itemId) || !!progress,
      refetchOnWindowFocus: false,
    },
  );

  const isViewOnly = sessionToken?.dat?.is_view_only;
  const userId = sessionToken?.dat?.user_id;

  const { data: isBoardOwner, isLoading: isLoadingBoardPermission } =
    useGetBoardPermissionQuery(context?.boardId ?? 0, userId ?? 0);

  const isLoading =
    isContextLoading ||
    isSessionTokenLoading ||
    isLoadingSubscriptionData ||
    isGetEnvelopeDetailsLoading ||
    isFileListLoading ||
    isLoadingBoardPermission;

  useEffect(() => {
    if (isLoading || !progress || !envelopeDetails || !fileList) return;

    const isAllowed = isBoardOwner && !isViewOnly;
    const navigationMap = {
      [APPLICATION_STATUS.INITIALIZED]: APP_ROUTES.INFO,
      [APPLICATION_STATUS.INFO_PAGE_DISPLAYED]: isAllowed
        ? progress?.envelopeStatus === ENVELOPE_STATUS.SETTINGS_CONFIGURED
          ? APP_ROUTES.SETUP
          : APP_ROUTES.SETTINGS
        : APP_ROUTES.SETUP,
    };

    const route = navigationMap[progress?.applicationStatus];

    if (!route) return;

    navigate(route);
  }, [
    isLoading,
    progress,
    envelopeDetails,
    fileList,
    isBoardOwner,
    isViewOnly,
    navigate,
  ]);

  useEffect(() => {
    if (!context?.appVersion?.versionData) return;
    const version = `v${context?.appVersion?.versionData?.major}.${context?.appVersion?.versionData?.minor}.${context?.appVersion?.versionData?.patch}`;
    updateBackOfficeInstalledStat(version);
  }, [context]);

  if (isLoading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center overflow-auto">
        <Loader size={Loader.sizes.MEDIUM} />
      </div>
    );
  }

  if (isProgressError) {
    return (
      <div className="flex h-screen flex-col overflow-auto px-8">
        <AuthenticateUI
          context={context}
          isLoading={isLoading}
          refetchProgress={refetchProgress}
        />
      </div>
    );
  }

  return null;
};

export default NewOnboardingRoot;
